// frontend/src/lib/onboardingAnalytics.ts
//
// Onboarding funnel instrumentation — the single choke point (design §3.7).
//
// Every onboarding funnel event flows through `trackOnboarding`, so swapping in a
// real analytics provider (PostHog, Segment, a backend endpoint, …) is a one-file
// change: replace the delegate below. Today it routes through the existing GA4
// pathway (`window.gtag`, initialised in utils/analytics.ts) when that is
// available, and otherwise logs to the console in dev builds only.
//
// Contract: this function must NEVER throw and must NEVER block. Instrumentation
// failures are irrelevant to the user's flow, so every path is wrapped in a
// swallowing try/catch.
import { trackEvent } from '@/utils/analytics';

/**
 * The onboarding funnel events. `time-to-first-data-point` (the activation
 * metric, design §3.7) is derived from the timestamp delta between
 * `wizard_viewed` and `first_data_point`.
 */
export type OnboardingEvent =
  | 'wizard_viewed'
  | 'step_completed'
  | 'step_skipped'
  | 'wizard_completed'
  | 'wizard_abandoned'
  | 'first_data_point';

/** GA4 event names are namespaced so onboarding events group cleanly. */
const EVENT_PREFIX = 'onboarding_';

/**
 * Record an onboarding funnel event. Guarded, non-throwing, non-blocking.
 *
 * @param event The funnel event.
 * @param props Optional event properties (e.g. `{ step, variant, source }`).
 */
export function trackOnboarding(
  event: OnboardingEvent,
  props?: Record<string, unknown>
): void {
  try {
    // Route through the real GA4 pathway when it exists. `trackEvent` is the
    // single GA4 integration point (utils/analytics.ts); guarding on
    // `typeof window.gtag === 'function'` here keeps this a no-op before GA4
    // initialises (or when it is never configured).
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      trackEvent(`${EVENT_PREFIX}${event}`, props ?? {});
      return;
    }
    // No provider wired: surface events in dev for local verification, stay
    // silent in prod. This is the seam a real provider replaces later.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- intentional dev-only funnel trace
      console.info(`[onboarding] ${event}`, props ?? {});
    }
  } catch {
    // Instrumentation must never break the funnel it measures.
  }
}
